import { useParams } from 'react-router'
import { Button, Tooltip } from '~/shared/ui'
import {
  useAddFlagComment,
  useCreateDocumentFlag,
  useDocumentFlags,
  useResolveDocumentFlag,
} from '../api'
import { STEP_FLAG_TYPE_TO_REVIEWED_STEP, type StepFlagType } from '../config/flag-types'
import { getActiveFlagByType } from '../lib/get-active-flag'
import { FlagCommentsPopover } from './flag-comments-popover'

interface StepFlagButtonProps {
  flagType: StepFlagType
  isCurrentStep: boolean
}

const DISABLED_TOOLTIP = 'Flagging is not available on validated steps.'

/**
 * Generic step-level flag chip rendered next to a step's heading.
 *
 * - No active flag + on current step → outline "FLAG" chip opens a local-draft
 *   popover; saving creates the flag and posts the comment (two-call dance).
 * - No active flag + not current step → disabled chip with tooltip.
 * - Active flag → filled "FLAGGED" chip opens the persisted popover; comments
 *   POST immediately, resolution posts the resolution comment first then
 *   resolves the flag.
 */
export function StepFlagButton({ flagType, isCurrentStep }: StepFlagButtonProps) {
  const documentKey = useParams<'documentKey'>().documentKey as string
  const { data: flagsData } = useDocumentFlags()
  const activeFlag = getActiveFlagByType(flagsData.items, flagType)

  const { mutateAsync: createDocumentFlag, isPending: isCreating } = useCreateDocumentFlag()
  const { mutateAsync: addFlagComment, isPending: isCommenting } = useAddFlagComment()
  const { mutateAsync: resolveDocumentFlag, isPending: isResolving } = useResolveDocumentFlag()

  const busy = isCreating || isCommenting || isResolving

  if (activeFlag) {
    return (
      <FlagCommentsPopover
        mode="persisted"
        flag={activeFlag}
        canEdit={isCurrentStep}
        busy={busy}
        trigger={
          <Button
            size="small"
            materialIcon={{ children: 'flag', fill: true }}
          >
            FLAGGED
          </Button>
        }
        onAddComment={async (text) => {
          await addFlagComment({
            path: { key: documentKey, flagKey: activeFlag.key },
            body: { notes: text },
          })
        }}
        onResolve={async (text) => {
          try {
            if (text.trim()) {
              await addFlagComment({
                path: { key: documentKey, flagKey: activeFlag.key },
                body: { notes: text },
              })
            }
            await resolveDocumentFlag({
              path: { key: documentKey, flagKey: activeFlag.key },
              body: {},
            })
          } catch {
            // Surface via query error state; no inline error UI here.
          }
        }}
      />
    )
  }

  if (!isCurrentStep) {
    return (
      <Tooltip content={DISABLED_TOOLTIP}>
        <span>
          <Button
            size="small"
            disabled
            materialIcon="flag"
          >
            FLAG
          </Button>
        </span>
      </Tooltip>
    )
  }

  return (
    <FlagCommentsPopover
      mode="local-draft"
      flag={null}
      localDraft={null}
      trigger={
        <Button
          size="small"
          materialIcon="flag"
        >
          FLAG
        </Button>
      }
      onSaveDraft={async (text) => {
        try {
          await createDocumentFlag({
            path: { key: documentKey },
            body: {
              flag_type: flagType,
              document_step: STEP_FLAG_TYPE_TO_REVIEWED_STEP[flagType],
              notes: text,
            },
          })
        } catch {
          // Surface via query error state; no inline error UI here.
        }
      }}
    />
  )
}
